import React, { useState, useRef, useEffect } from 'react';
import { Button, Form, Input, notification, Tooltip } from 'antd';
import DefaultLayout from '../../components/layout/DefaultLayout';
import {
  ArrowLeftIcon,
  InformationCircleIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import cards from '../../services/cards';
import { useHistory } from 'react-router';
import Errors from '../../components/errors';
import CardFront from '../../components/cards/front';
import CardBack from '../../components/cards/back';
import { Link } from 'react-router-dom';
import Essence from '../../models/cardEssences';
import ESSENCES from '../../models/essences.json';
import CreateOrEditEssences from './components/create-or-edit-essences';

const CardScreen = () => {
  const Router = useHistory();

  const [card, setCard] = useState({
    name_on_card: '',
    signature: '',
    picture: null,
    essences: [],
    Card_Essences_Values: [],
  });

  const [essences, setEssences] = useState([...ESSENCES]);
  const [showCreateEssence, setShowCreateEssence] = useState(false);

  const selectedEssence = useRef();
  const formRef = useRef(null);
  const inputFileRef = useRef(null);

  useEffect(() => {
    const Card_Essences_Values = essences.map((essence) => ({
      name: essence.name,
      value: essence.value,
    }));

    setCard((card) => ({ ...card, Card_Essences_Values }));
  }, [essences]);

  const setCardPicture = (e) => {
    try {
      const picture = e.target.files[0];

      if (picture) {
        setCard({ ...card, picture: picture });
      }
    } catch (error) {}
  };

  const deleteCardPicture = () => {
    setCard({ ...card, picture: null });

    inputFileRef.current.value = '';
  };

  const saveCard = async () => {
    await formRef.current.validateFields();
    try {
      //Push card essences
      card.essences = essences;

      const response = await cards.createCard(card);

      notification.open({
        type: 'success',
        message: response.message,
        placement: 'bottomRight'
      });

      setCard({
        name_on_card: '',
        signature: '',
        picture: null,
        essences: [],
        Card_Essences_Values: [],
      });

      setEssences([]);
      Router.push('/');
    } catch (error) {
      notification.open({
        type: 'error',
        message: 'Ocurrio un error al crear la tarjeta',
        description: <Errors errors={error?.errors} />,
        placement: 'bottomRight'
      });
    }
  };

  const updateEssenceValue = (essence) => {
    try {
      let value = 0;
      for (const word of essence.words) {
        value += word.value;
      }

      essence.value = value / essence.words.length;

      const index = essences.findIndex((item) => item.name === essence.name);

      essences.splice(index, 1, essence);

      setEssences([...essences]);
    } catch (error) {}
  };

  return (
    <DefaultLayout>
      <div className="p-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-10">
          <div>
            <Link
              to="/"
              className="flex items-center gap-2 font-medium text-lg hover:text-cyan-600"
            >
              <ArrowLeftIcon width={20} /> Regresar
            </Link>
            <h1 className="text-3xl xl:text-4xl font-bold">
              Crear nueva tarjeta
            </h1>
            <p className="mt-4">Por favor ingresa los campos solicitados</p>
            <Form ref={formRef}>
              <Form.Item
                name="name_on_card"
                rules={[
                  { required: true, message: 'Por favor ingrese el nombre' },
                ]}
              >
                <Input
                  placeholder="Nombre en tarjeta"
                  onChange={(e) =>
                    setCard({ ...card, name_on_card: e.target.value })
                  }
                />
              </Form.Item>

              <div>
                <h2 className="text-xl font-medium">Esencia</h2>
                <p className="mb-2">
                  Haga click sobre una esencia para modificar sus atributos.
                </p>
                <div className="flex items-center my-4">
                  {essences.map((essence, index) => (
                    <Tooltip title={essence.name} key={index}>
                      <div
                        style={new Essence(essence).style}
                        className="flex items-center justify-center text-xs font-medium text-white cursor-pointer hover:shadow-md"
                        onClick={() => {
                          selectedEssence.current = essence;
                          setShowCreateEssence(true);
                        }}
                      >
                        {parseFloat(essence.value).toFixed(1)}
                      </div>
                    </Tooltip>
                  ))}

                  <CreateOrEditEssences
                    essences={essences}
                    defaultOpenTab={selectedEssence.current?.name}
                    visible={showCreateEssence}
                    onEssenceChange={updateEssenceValue}
                    onCancel={() => setShowCreateEssence(false)}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-medium">Firma digital</h2>
                  <Tooltip
                    title="La firma ingresada debe ser unica"
                    placement="right"
                  >
                    <InformationCircleIcon width={20} />
                  </Tooltip>
                </div>
                <p className="mb-2">
                  Ingresa la firma digital del usuario para generar el código QR
                </p>
                <Form.Item
                  className="relative"
                  name="signature"
                  rules={[
                    {
                      required: true,
                      message: 'Por favor ingrese la firma digital',
                    },
                  ]}
                >
                  <div>
                    <Input
                      placeholder="Firma digital"
                      onChange={(e) =>
                        setCard({ ...card, signature: e.target.value })
                      }
                    />
                    <div className="absolute -bottom-7 right-0">
                      <button className="flex items-center gap-1 font-medium text-blue-800 hover:text-blue-900">
                        <SparklesIcon width={18} /> Generar
                      </button>
                    </div>
                  </div>
                </Form.Item>
              </div>

              <div>
                <h2 className="text-xl font-medium">Imagen</h2>
                <p className="mb-3">
                  Seleccione una imagen con formato válido de imagen (jpg, png,
                  jpeg)
                </p>

                {!card.picture ? (
                  <div
                    onClick={() => inputFileRef.current.click()}
                    className="w-28 h-32 border-black border-2 border-dashed flex items-center justify-center text-center cursor-pointer hover:shadow-md hover:bg-gray-100 transition shadow"
                  >
                    Seleccionar imagen
                  </div>
                ) : (
                  <div className="relative w-28 h-32 bg-white rounded-md">
                    <img
                      alt={'card_picture: ' + card.code}
                      src={URL.createObjectURL(card.picture)}
                      className="object-cover h-full rounded-md"
                    />
                    <Tooltip title="Eliminar" placement="right">
                      <div
                        onClick={() => deleteCardPicture()}
                        className="absolute -top-2 -right-2 bg-white shadow-lg rounded-full cursor-pointer w-7 h-7 flex items-center justify-center hover:shadow-xl hover:scale-105 transform transition"
                      >
                        <TrashIcon width={22} color="red" />
                      </div>
                    </Tooltip>
                  </div>
                )}
                <input
                  ref={inputFileRef}
                  type="file"
                  style={{ display: 'none' }}
                  accept="image/png, image/jpeg"
                  onChange={(e) => setCardPicture(e)}
                />
              </div>
            </Form>
          </div>
          <div className="lg:px-10  space-y-5 pt-10">
            <h3 className="text-xl font-medium">Previsualización</h3>

            <CardFront card={card} readonly />

            <CardBack card={card} readonly />

            <Button
              onClick={() => saveCard()}
              className="bg-green-600 w-full md:w-96 font-medium text-white hover:bg-green-700 hover:text-white focus:bg-green-500 focus:text-white"
            >
              Guardar
            </Button>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default CardScreen;
